import { useLocation } from 'react-router-dom';
import { navData } from 'src/layouts/config-nav-dashboard';
import { paths } from 'src/routes/paths';

type NavNode = {
  title: string;
  path: string;
  children?: NavNode[];
};

const BILLING_TABS = [
  { title: 'Overview', path: paths.dashboard.billing.root },
  { title: 'Total', path: paths.dashboard.billing.total },
  { title: 'History', path: paths.dashboard.billing.history },
] as const;

export type NavPageParts = {
  /** Left-nav parent. Null when the page has no distinct section. */
  section: string | null;
  page: string;
};

function parts(section: string, page: string): NavPageParts {
  return section === page ? { section: null, page } : { section, page };
}

/**
 * Left-nav location for a dashboard route.
 * An index child (same path as its parent) matches only that exact URL,
 * so a detail route under it does not inherit the index tab name.
 */
export function navPageParts(pathname: string): NavPageParts | null {
  const path = pathname.split('?')[0].replace(/\/$/, '') || '/';

  const billing = [...BILLING_TABS]
    .sort((a, b) => b.path.length - a.path.length)
    .find((tab) => path === tab.path || path.startsWith(`${tab.path}/`));
  if (billing) return parts('Billing', billing.title);

  let best: { parts: NavPageParts; len: number } | null = null;

  for (const section of navData) {
    for (const item of section.items as NavNode[]) {
      for (const child of item.children ?? []) {
        const exact = path === child.path;
        const indexRoute = child.path === item.path;
        const nested = !indexRoute && path.startsWith(`${child.path}/`);
        if (!exact && !nested) continue;
        if (!best || child.path.length > best.len) {
          best = { parts: parts(item.title, child.title), len: child.path.length };
        }
      }
    }
  }
  if (best) return best.parts;

  for (const section of navData) {
    for (const item of section.items as NavNode[]) {
      if (path === item.path || path.startsWith(`${item.path}/`)) {
        if (!best || item.path.length > best.len) {
          best = { parts: { section: null, page: item.title }, len: item.path.length };
        }
      }
    }
  }
  return best?.parts ?? null;
}

/** Document title. Visible heading is `NavPageHeading`. */
export function navPageTitle(pathname: string): string | null {
  const found = navPageParts(pathname);
  if (!found) return null;
  return found.section ? `${found.section} · ${found.page}` : found.page;
}

export function useNavPageParts(): NavPageParts | null {
  const { pathname } = useLocation();
  return navPageParts(pathname);
}

export function useNavPageTitle(): string | null {
  const { pathname } = useLocation();
  return navPageTitle(pathname);
}
